import GeometryLoading from "./assets/geometry-loading.js";
import TextureLoading from "./assets/texture-loading.js";
import EnvLightLoading from "./assets/envlight-loading.js";
import MaterialLoading from "./assets/material-loading.js";
import ObjectLoading from "./assets/object-loading.js";
import SkeletonLoading from "./assets/skeleton-loading.js";
import AnimationLoading from "./assets/animation-loading.js";

import MaterialProperties from "./assets/material-properties.js";
import ObjectProperties from "./assets/object-properties.js";
import SkeletonProperties from "./assets/skeleton-properties.js";

import ActiveEnvLightController from "./assets/active-envlight-controller.js";
import CameraProperties from "./assets/camera-properties.js";

class Assets {
    constructor(wrapCtx, evtCtx, shlWrapCtx, shlEvtCtx, controllers) {
        const {
            geometryLoaders,
            textureLoader,
            envLightLoaders,
            materialControllers: { loaders: materialLoaders, properties: materialPropertyControllers },
            objectControllers: { loaders: objectLoaders, properties: objectPropertyControllers },
            skeletonControllers: { loader: skeletonLoader, assigner: skeletonAssigner },
            animationLoader,
            cameraController,
        } = controllers;

        this._geoLoading = new GeometryLoading(evtCtx, shlWrapCtx, shlEvtCtx, geometryLoaders);
        this._texLoading = new TextureLoading(evtCtx, shlWrapCtx, shlEvtCtx, textureLoader);
        this._envLoading = new EnvLightLoading(evtCtx, shlWrapCtx, shlEvtCtx, envLightLoaders);
        this._matLoading = new MaterialLoading(evtCtx, shlWrapCtx, shlEvtCtx, materialLoaders);
        this._objLoading = new ObjectLoading(evtCtx, shlWrapCtx, shlEvtCtx, objectLoaders);
        this._sklLoading = new SkeletonLoading(evtCtx, shlWrapCtx, shlEvtCtx, skeletonLoader);
        this._anmLoading = new AnimationLoading(evtCtx, shlWrapCtx, shlEvtCtx, animationLoader);

        this._matProperties = new MaterialProperties(evtCtx, shlWrapCtx, shlEvtCtx, materialPropertyControllers);
        this._objProperties = new ObjectProperties(evtCtx, shlWrapCtx, shlEvtCtx, objectPropertyControllers);
        this._sklProperties = new SkeletonProperties(skeletonAssigner);

        this._activeEnvLightCtrl = new ActiveEnvLightController(evtCtx, shlWrapCtx, shlEvtCtx);
        this._camProperties = new CameraProperties(wrapCtx, evtCtx, cameraController);

        this._assets = Object.freeze({
            geometries: this._geoLoading.geometries,
            textures: this._texLoading.textures,
            envLights: this._envLoading.envLights,
            materials: this._matLoading.materials,
            objects: this._objLoading.objects,
            skeletons: this._sklLoading.skeletons,
            animations: this._anmLoading.animations,
        });
    }

    get assets() { return this._assets; }
    get activeEnvLight() { return this._activeEnvLightCtrl.envLight; }

    track(data) {
        if (data) {
            this._geoLoading.track(data.geometriesByType);
            this._texLoading.track(data.textures);
            this._envLoading.track(data.envLightsByType);
            this._matLoading.track(data.materialsByType);
            this._objLoading.track(data.objectsByType, data.objectGeometriesByType, data.objectMaterialsByType, this._geoLoading.geometries.all, this._matLoading.materials.all);
            this._sklLoading.track(data.skeletons, this._objLoading.objects.all);
            this._anmLoading.track(data.animations);

            this._matProperties.trackMaterialData(data.materialGenericPropsByType, data.materialTexturePropsByType);
            this._matProperties.trackMaterials(this._matLoading.materials.byType);
            this._matProperties.trackTextures(this._texLoading.textures.all);
            this._matProperties.trackEnvLight(this._activeEnvLightCtrl.envLight);
            this._objProperties.trackParents(data.objectParents);
            this._objProperties.trackTransformModifiers(data.objectTransformModifierFlags);
            this._objProperties.trackVisibility(data.objectVisibilityFlags);
            this._objProperties.trackCoords(data.objectCoordinates);
            this._objProperties.trackMorphTargets(data.objectMorphTargetInfluences);
            this._objProperties.trackObjects(this._objLoading.objects);
            this._sklProperties.trackObjects(this._objLoading.objects.byType["SkinnedMesh"]);
            this._sklProperties.trackSkeletons(this._sklLoading.skeletons.all);

            this._activeEnvLightCtrl.track(data.activeEnvLight, this._envLoading.envLights.all);
            this._camProperties.track(data.camera);
        } else {
            this._geoLoading.track();
            this._texLoading.track();
            this._envLoading.track();
            this._matLoading.track();
            this._objLoading.track();
            this._sklLoading.track();
            this._anmLoading.track();

            this._matProperties.trackMaterialData();
            this._matProperties.trackMaterials();
            this._matProperties.trackTextures();
            this._matProperties.trackEnvLight();
            this._objProperties.trackParents();
            this._objProperties.trackTransformModifiers();
            this._objProperties.trackVisibility();
            this._objProperties.trackCoords();
            this._objProperties.trackMorphTargets();
            this._objProperties.trackObjects();
            this._sklProperties.trackObjects();
            this._sklProperties.trackSkeletons();

            this._activeEnvLightCtrl.track();
            this._camProperties.track();
        }
    }

    update() {
        this._geoLoading.update();
        this._texLoading.update();
        this._envLoading.update();
        this._matLoading.update();
        this._objLoading.update();
        this._sklLoading.update();
        this._anmLoading.update();

        this._matProperties.update();
        this._objProperties.update();
        this._sklProperties.update();

        this._activeEnvLightCtrl.update();
        this._camProperties.update();
    }
}

export default Assets;
